import { useCallback, useMemo } from 'react';
import type { ChatPillConfig } from '../components/atoms/ChatActionPills/types';
import { useAIChatConversation } from '../contexts/AIChatConversation/useAIChatConversation';

type PillInput = Omit<ChatPillConfig, 'variant' | 'priority'> & {
  variant?: ChatPillConfig['variant'];
  priority?: ChatPillConfig['priority'];
};

type PillManager = {
  pills: ChatPillConfig[];
  addPill: (pill: PillInput) => void;
  removePill: (id: string) => void;
  removePillsByType: (type: string) => void;
  clearPills: () => void;
};

/**
 * Hook for managing chatbot action pills
 * Provides a clean API for adding, removing, and clearing pills
 */
export function usePillManager(): PillManager {
  const { pills, addPill: ctxAdd, removePill: ctxRemove, removePillsByType: ctxRemoveByType, clearPills: ctxClear } = useAIChatConversation();

  const addPill = useCallback(
    (pill: PillInput) => {
      // Validate required fields
      if (!pill.id || !pill.type || !pill.text || typeof pill.onClick !== 'function') {
        console.warn('[usePillManager] Invalid pill config - missing required fields');
        return;
      }

      // Add defaults and dispatch
      const fullPill: ChatPillConfig = {
        variant: 'primary',
        priority: 0,
        ...pill,
      };

      ctxAdd(fullPill);
    },
    [ctxAdd]
  );

  const removePill = useCallback((id: string) => ctxRemove(id), [ctxRemove]);

  const removePillsByType = useCallback((type: string) => ctxRemoveByType(type), [ctxRemoveByType]);

  const clearPills = useCallback(() => ctxClear(), [ctxClear]);

  return useMemo(
    () => ({
      pills,
      addPill,
      removePill,
      removePillsByType,
      clearPills,
    }),
    [pills, addPill, removePill, removePillsByType, clearPills]
  );
}

export default usePillManager;
